<script>
    /** @type {import('./$types').PageData} */
    import Fa from 'svelte-fa';
    import { faBullseye, faDollarCircle } from '@fortawesome/pro-solid-svg-icons';

    import { page } from '$app/stores';
    import { productSchema } from '$lib/utils/structuredData/productSchema';
    import { toPlainText } from '$lib/utils/functions/toPlainText';

    import PageHeader from '$lib/components/PageHeader.svelte';
    import ProductBuy from '$lib/components/ProductBuy.svelte';
    import ProductDiscontinued from '$lib/components/ProductDiscontinued.svelte';
    import Seo from '$lib/components/Seo.svelte';
    import ProductDescriptionOptions from '$lib/components/ProductDescriptionOptions.svelte';
    import ProductStockTable from '$lib/components/ProductStockTable.svelte';
    import ProductGallery from '$lib/components/ProductGallery.svelte';
    import PortableText from '$lib/components/PortableText.svelte';
    import ProductShippingAlert from '$lib/components/ProductShippingAlert.svelte';

    import SanitySections from '$lib/components/SanitySections.svelte';
    import Section from '$lib/components/Section.svelte';
    import ProductGrid from '$lib/components/ProductGrid.svelte';

    import ProductReviews from '$lib/components/ProductReviews.svelte';
    import InsightWise from '$lib/utils/InsightWise';

    export let data;

    const { product } = data;

    const upsells = product?.upsells?.references?.nodes || [];

    // Strcutured data schema for SEO
    const ldJSON = productSchema(product, $page.url) || null;

    let clubPromotion = product?.promotionClub?.value || undefined;
    let selectedVariant;

    // InsightWise
    $: if (selectedVariant) {
        const insightData = InsightWise.createInsightItem.product({ product, selectedVariant, cms: data?.cms });
        InsightWise.add([InsightWise.createInsight.productView(insightData), InsightWise.createInsight.itemView(insightData)]);
    }
</script>

<Seo
    title={product?.seo?.title}
    description={product?.seo?.description || toPlainText(data?.cms?.description).slice(0, 320)}
    url={$page.url}
    image={product?.featuredImage?.url}
    {ldJSON}
/>

<Section padding="Bottom" maxWidth="Medium" headerVisibile={false}>
    <PageHeader
        title={`${product?.vendor} ${product?.title.replace(product?.vendor.replace('Salts', ''), '')}`}
        shareButton={true}
        searchButton={true}
        classes="px-2 py-8 lg:px-0"
    />

    <div class="relative m-auto grid max-w-7xl grid-cols-1 lg:grid-cols-2 lg:gap-4">
        <div>
            <div class="overflow-hidden rounded-tl-xl rounded-tr-xl border-base-900/5 bg-base-900 shadow-sm lg:rounded-xl">
                <ProductGallery
                    images={product?.images?.edges}
                    {selectedVariant}
                    title={`${product?.vendor} ${product?.title.replace(product?.vendor.replace('Salts', ''), '')}`}
                    classes="m-auto w-80 lg:w-full"
                />
            </div>
        </div>

        <div class="flex flex-col gap-2 lg:gap-4">
            <div class="rounded-bl-xl rounded-br-xl border-base-900/5 bg-base-900 p-4 pt-0 shadow-sm lg:rounded-xl lg:p-7">
                {#if data?.archived}
                    <ProductDiscontinued {data} />
                {:else}
                    <ProductBuy {product} {upsells} bind:selectedVariant />
                {/if}
            </div>

            {#if !data?.archived}
                <div class="rounded-xl border-base-900/5 bg-base-900 p-4 shadow-sm lg:p-7">
                    <div class="flex flex-col gap-6">
                        {#if clubPromotion && product?.compareAtPriceRange?.maxVariantPrice?.amount < product?.priceRange?.maxVariantPrice?.amount}
                            <div class="flex flex-row items-center justify-between">
                                <div>
                                    <Fa icon={faBullseye} size="1x" class="mr-0.5 inline-block text-primary dark:text-purple-300" />
                                    <span class="inline-block font-semibold">Club Promotion</span>
                                    <span class="block text-xs">Special Limited Time Offer</span>
                                </div>
                                <div class="font-semibold">{clubPromotion}</div>
                            </div>
                        {/if}

                        {#if !data?.site?.featureHidden?.shippingAlert}
                            <ProductShippingAlert />
                        {/if}
                    </div>
                </div>
            {/if}

            <div class="rounded-xl border-base-900/5 bg-base-900 p-4 shadow-sm lg:p-7">
                {#if data?.cms?.description}
                    <span class="text-sm text-color-text/80">Description</span>
                    <PortableText data={data?.cms?.description} />
                {/if}

                {#if product?.options?.[0]?.name != 'Title' && product?.options?.[0]?.name != 'Colour' && !data?.archived}
                    <div class="mb-4">
                        <ProductDescriptionOptions options={product.options} />
                    </div>
                {/if}

                {#if !data?.archived}
                    <div class="mb-2.5">
                        <ProductStockTable variants={product.variants.nodes} available={product.availableForSale} />
                    </div>
                {/if}
            </div>
        </div>
    </div>
</Section>

{#if data?.cms?.sections}
    <SanitySections sections={data?.cms?.sections} />
{/if}

{#await data?.streamed?.reviews then reviewData}
    {#if reviewData?.reviews?.length}
        <Section padding="Bottom" maxWidth="Medium" title={`${data?.product?.title} Customer Reviews`} headerVisibile={true} titleType="h2" style="Framed">
            <ProductReviews {data} />
        </Section>
    {/if}
{/await}

{#await data?.streamed?.recomendations then reccies}
    {#if reccies?.productRecommendations}
        <Section padding="Bottom" maxWidth="Medium" title="You May Also Like" headerVisibile={true} titleType="h2">
            <div class="no-scrollbar relative flex flex-nowrap gap-x-2 overflow-scroll lg:grid lg:grid-cols-5 lg:gap-2 lg:overflow-visible">
                {#each reccies?.productRecommendations?.slice(0, 5) as product (product)}
                    <div class="w-[195px] flex-none lg:w-auto">
                        <ProductGrid {product} lazyLoadImage={true} />
                    </div>
                {/each}
            </div>
        </Section>
    {/if}
{/await}
